import { ZodIssue } from "zod";
import { User, UserIdAndName } from "../../types/User";
import {
  formatDateTime,
  getToken,
  PartnerpaymentType,
  paymentType,
  saveToken,
} from "../../lib/utils";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import { ActionResult } from "../../types";
import { LoginSchema } from "../../lib/schemas/loginSchema";
import agent from "../api/agent";
import {
  RegisterForUpdateSchema,
  RegisterSchema,
  RegisterSchemaForEmail,
} from "../../lib/schemas/registerSchema";
import { PaginationData, PagingParams } from "../../types/pagination";
import PartnerList from "../../feature-module/partner/PartnerList";

export default class UserStore {
  user: User | null = null;
  error: ZodIssue[] | string | null = null;
  token: string | undefined | null = getToken();
  appLoaded: boolean = false;
  language: string = "en";
  pendingPartners: User[] = [];
  pendingPartnersLoading = false;
  pendingPartnersError: string | null = null;
  pagination: PaginationData | null = null;
  pagingParams = new PagingParams();
  searchTerm: string = "";
  partners: User[] | null | undefined = null;
  loadingInitial = false;
userLoaded: boolean = false; // add this near `appLoaded`

  usersIdName: UserIdAndName[] | undefined = [];
  editUser: UserIdAndName | null = null;

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.token,
      (token) => {
        return saveToken(token);
      }
    );
  }

  get isLoggedIn() {
    return !!this.user;
  }

  setUserLoaded = (value: boolean) => {
  runInAction(() => {
    this.userLoaded = value;
  });
};

  login = async (creds: LoginSchema): Promise<ActionResult<string>> => {
    try {
      const user = await agent.Account.login(creds);

      // reslove Mobx strict mode
      runInAction(() => {
        this.setToken(user.token);
        this.user = user;
      });

      return { status: "success", data: "Logged in" };
    } catch (error) {
      return { status: "error", error: error as string };
    }
  };

getUser = async () => {
  try {
    const user = await agent.Account.current();
    runInAction(() => {
      this.user = user;
      this.userLoaded = true; // set this
    });

    return { status: "success", data: "We have got current user" };
  } catch (error) {
    runInAction(() => {
      this.user = null;
      this.userLoaded = true; // even if error, we're done loading
    });

    return { status: "error", error: error as string };
  }
};


  register = async (creds: RegisterSchema): Promise<ActionResult<string>> => {
    try {
      await agent.Account.register(creds);

      // reslove Mobx strict mode
      // runInAction(() => {
      //   this.setToken(user.token);
      //   this.user = user;
      // });

      return { status: "success", data: "User Created Succesfully " };
    } catch (error) {
      return { status: "error", error: error as string };
    }
  };

  registerWithEmail = async (
    creds: RegisterSchemaForEmail
  ): Promise<ActionResult<string>> => {
    try {
      await agent.Account.registerWithEmail(creds);

      // reslove Mobx strict mode
      // runInAction(() => {
      //   this.setToken(user.token);
      //   this.user = user;
      // });

      return { status: "success", data: "User Created Succesfully " };
    } catch (error) {
      return { status: "error", error: error as string };
    }
  };

  deleteUser = async (id: string): Promise<ActionResult<string>> => {
    try {
      await agent.Account.delete(id);
      runInAction(() => {
        this.usersIdName =
          this.usersIdName?.filter((s) => s.id !== id) || undefined;
      });
      return { status: "success", data: "User deleted successfully" };
    } catch (error) {
      console.error("Error deleting User:", error);
      return { status: "error", error: error as string };
    }
  };

  updateUser = async (
    id: string,
    data: RegisterForUpdateSchema
  ): Promise<ActionResult<string>> => {
    try {
      await agent.Account.update(id, data);
      await this.loadUsers();
      return { status: "success", data: "User updated successfully" };
    } catch (error) {
      console.error("Error updating user: ", error);
      return { status: "error", error: error as string };
    }
  };

  getUSer = async (id: string) => {
    try {
      const result = await agent.Account.getById(id);
      runInAction(() => {
        this.editUser = result;

      });
      return result;
    } catch (error) {
      console.error("Error loading service:", error);
    }
  };

  logout = async () => {
    runInAction(() => {
      this.setToken(null);
      this.user = null;
    });
  };

  setAppLoaded = async () => {
    this.appLoaded = true;
  };

  setLanguage = async (lang: string) => {
    this.language = lang;
  };

  setServerError(error: ZodIssue[] | string) {
    this.error = error;
  }

  setToken = async (token: string | null | undefined) => {
    await saveToken(token);
    runInAction(() => (this.token = token));
  };

  checkUserExistsByEmail = async (email: string): Promise<boolean> => {
    try {
      const exists = await agent.Account.checkUserExistsByEmail(email); // create this endpoint
      return exists;
    } catch (error) {
      console.error("Error checking user existence", error);
      return false;
    }
  };

  // Update Booking Status
  setPaymentTypeOfPartner = async (
    id: string,
    type: PartnerpaymentType
  ): Promise<ActionResult<string>> => {
    try {
      await agent.Account.setPaymentTypeOfPartner(id, type);
      await this.getUSer(id);
      return { status: "success", data: `Booking Payment Type set to ${type}` };
    } catch (error) {
      console.error("Error updating booking Payment Type: ", error);
      return { status: "error", error: error as string };
    }
  };

  get axiosParams() {
    const params = new URLSearchParams();
    params.append("pageNumber", this.pagingParams.pageNumber.toString());
    params.append("pageSize", this.pagingParams.pageSize.toString());
    if (this.searchTerm) params.append("searchTerm", this.searchTerm);
    return params;
  }

  loadUsers = async () => {
    try {
      const result = await agent.Account.getUsersIdAndName(this.axiosParams);
      runInAction(() => {
        this.usersIdName = result.data;
      });
    } catch (error) {
      console.error("error loading users ", error);
    }
  };

  partnerRegister = async (
    formData: FormData
  ): Promise<ActionResult<string>> => {
    try {
      await agent.Account.partnerRegister(formData);
      return { status: "success", data: "Partner registered successfully" };
    } catch (error) {
      console.error("Error registering partner", error);
      return { status: "error", error: error as string };
    }
  };

  loadPendingPartners = async () => {
    try {
      const result = await agent.Account.getPendingPartners(this.axiosParams);
      runInAction(() => {
        const { pageNumber, pageSize, data, pageCount, totalCount } = result;
        this.setPagination({ pageNumber, pageSize, pageCount, totalCount });

        const partnerList: User[] = data.map((item) => ({
          ...item,
          partnerApproved: item.partnerApproved === true,
          createDate: formatDateTime(item.createDate ?? undefined),
        }));

        this.partners = partnerList;
      });
      this.loadingInitial = false;
    } catch (error) {
      console.error("Error loading bookings: ", error);
      this.loadingInitial = false;
    }
  };

  approvePartner = async (id: string): Promise<ActionResult<string>> => {
    try {
      await agent.Account.approvePartner(id); // Ensure this endpoint exists
      runInAction(() => {
        this.loadPendingPartners(); // Refresh partner list
      });
      return { status: "success", data: "Partner approved successfully" };
    } catch (error) {
      console.error("Error approving partner:", error);
      return { status: "error", error: "Failed to approve partner" };
    }
  };

  rejectPartner = async (
    id: string,
    rejectionReason: string
  ): Promise<ActionResult<string>> => {
    try {
      const msg = await agent.Account.rejectPartner(id, rejectionReason);
      runInAction(() => this.loadPendingPartners());
      return { status: "success", data: msg };
    } catch (error: any) {
      if (error?.response) {
        return {
          status: "error",
          error: error.response.data || "Rejection failed",
        };
      }
      if (error?.request)
        return { status: "error", error: "No response from server" };
      return { status: "error", error: error?.message || "Unknown error" };
    }
  };

  // Optional
  get pendingPartnersCount() {
    return this.pendingPartners.length;
  }



  hasRole = (role: string): boolean => {
    return this.user?.roles && Array.isArray(this.user.roles)
      ? this.user.roles.includes(role)
      : false;
  };

  isAdmin = () => {
    return this.hasRole("ADMIN");
  };
  isCustomer = () => {
    return this.hasRole("CUSTOMER");
  };
  isCashier = () => {
    return this.hasRole("CASHIER");
  };

  isPartner = () => {
    return this.hasRole("PARTNER");
  };

  isUser = (): boolean => {
    return this.hasRole("USER");
  };

    isAirportCashier = (): boolean => {
    return this.hasRole("AIRPORTCASHIER");
  };


  setPagingParams = (pagingParams: PagingParams) => {
    this.pagingParams = pagingParams;
  };

  setPagination = (pagination: PaginationData) => {
    this.pagination = pagination;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };
}
